import { useEffect, useState } from 'react'

import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok'
interface AdvisersViewProps {
  selectedSpecialty?: string
}
import Image from 'next/image'
import React from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'

import styles from '../Searchfindcomponent.module.css'

type StoryblokHeaders = {
  total?: string | number
  [key: string]: unknown
}

const AdvisersView: React.FC<AdvisersViewProps> = ({ selectedSpecialty }) => {
  const location =
    process.env.NEXT_PUBLIC_NEXT_LOCATION != null && process.env.NEXT_PUBLIC_NEXT_LOCATION !== ''
      ? process.env.NEXT_PUBLIC_NEXT_LOCATION
      : 'au'
  const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalAdvisers, setTotalAdvisers] = useState(0)
  const [relatedOffices, setRelatedOffices] = useState<
    Array<{ uuid: string; content: Record<string, unknown>; full_slug: string; name: string }>
  >([])
  const [page, setPage] = useState(1)
  const [advisers, setAdvisers] = useState<
    Array<{ uuid: string; content: Record<string, unknown>; full_slug: string; name: string }>
  >([])
  const [specialtyLinks, setSpecialtyLinks] = useState<Array<{ name: string; value: string }>>([])

  const perPage = 10

  const getAdvisers = async (currentPage = 1) => {
    try {
      setIsLoading(true)

      // Build filter query - if no specialty selected, fetch all advisers
      const filterQuery: Record<string, unknown> = {
        component: { in: 'adviser' },
        __or: [
          { 'settings.location_available': { in: location } },
          { 'settings.location_available': { not_in: filterLocation } },
        ],
      }

      // Only add specialty filter if one is selected
      if (selectedSpecialty != null && selectedSpecialty !== '') {
        filterQuery.specialities = { any_in_array: selectedSpecialty }
      }

      const response = await getStoryblokApi().get('cdn/stories', {
        ...searchParamsData,
        filter_query: filterQuery,
        per_page: perPage,
        resolve_relations: 'offices',
        page: currentPage,
        sort_by: 'name:asc',
      })
      const data = response.data
      // Convert Headers object to plain object to match StoryblokHeaders type
      const headers: StoryblokHeaders = Object.fromEntries(Object.entries(response.headers))

      if (data?.stories != null) {
        if (currentPage === 1) {
          setAdvisers(data.stories)
          setRelatedOffices(data.rels ?? [])
        } else {
          setAdvisers((prev) => [...prev, ...data.stories])
          setRelatedOffices((prev) => [...prev, ...(data.rels ?? [])])
        }
        setTotalAdvisers(Number(headers.total ?? '0'))
        setPage(currentPage)
      }
    } catch (error) {
      console.error('Error fetching advisers:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadMore = () => {
    if (!isLoading && totalAdvisers > advisers.length) {
      getAdvisers(page + 1)
    }
  }

  const fetchSpecialtyLinks = async () => {
    const storyblokApi = getStoryblokApi()
    const { data: specialtyLinks } = await storyblokApi.get(`cdn/datasource_entries`, {
      ...searchParamsData,
      per_page: 100,
      datasource: 'service-lines-links',
    })

    return specialtyLinks
  }

  // Fetch specialty links only once on mount
  useEffect(() => {
    const getSpecialtyLinks = async () => {
      const data = await fetchSpecialtyLinks()
      setSpecialtyLinks(data.datasource_entries)
    }
    getSpecialtyLinks()
  }, [])

  // Fetch advisers when specialty changes
  useEffect(() => {
    // Reset state when specialty changes
    setAdvisers([])
    setRelatedOffices([])
    setTotalAdvisers(0)
    setPage(1)

    // Fetch advisers for the selected specialty
    getAdvisers(1)
  }, [selectedSpecialty])

  return (
    <div className={styles['advisersView']}>
      <div className={styles['bottomContainer']}>
        <div className={styles['bottomInner']}>
          {advisers.map((adviser) => {
            // get the office from the relatedOffices array
            const adviserOffices = Array.isArray(adviser.content.offices)
              ? (adviser.content.offices as string[])
                  .map((officeUuid: string) =>
                    relatedOffices.find((rel) => rel.uuid === officeUuid)
                  )
                  .filter((office): office is NonNullable<typeof office> => office != null)
              : []

            const imageSrc =
              (adviser.content.filename as string) ||
              (adviser.content.image as { filename: string })?.filename ||
              ''

            return (
              <div key={adviser.uuid + adviser.full_slug} className={styles['officeAdviser']}>
                <div className={styles['adviserCallbackContainer']}>
                  <a href={`/${adviser.full_slug}`} className={styles['image']}>
                    {imageSrc ? (
                      <Image
                        unoptimized={true}
                        src={imageSrc}
                        alt={adviser.name ?? 'Adviser'}
                        width={120}
                        height={120}
                        loading="lazy"
                        placeholder="blur"
                        blurDataURL="data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2Fsvg%22%20viewBox%3D%220%200%20120%20120%22%2F%3E"
                      />
                    ) : null}
                  </a>
                  <PhnxButton
                    type="link"
                    variant="primary"
                    size="small"
                    label="Contact Us"
                    iconPosition="right"
                    href={`/${adviser.full_slug}?requestCallback=true`}
                  />
                </div>
                <div className={styles['adviserRight']}>
                  <div className={styles['name']}>
                    <h5>
                      <a href={`/${adviser.full_slug}`}>{adviser.name ?? 'Adviser'}</a>
                    </h5>
                    <h6>{(adviser.content?.title as string) ?? ''}</h6>
                  </div>
                  <div className={styles['phone']}>
                    <h5>Phone</h5>
                    <h6>
                      {(() => {
                        const office = adviserOffices[0]
                        if (office == null) return null
                        // New office structure: office.content.office[0].phone
                        if (
                          Array.isArray(office.content?.office) &&
                          (office.content.office[0] as { phone?: string })?.phone != null
                        ) {
                          const phone = (office.content.office[0] as { phone: string }).phone
                          return <a href={`tel:${phone}`}>{phone}</a>
                        }
                        // Old office structure: office.content.Phone
                        if (office.content?.Phone != null) {
                          const phone = office.content.Phone as string
                          return <a href={`tel:${phone}`}>{phone}</a>
                        }
                        return null
                      })()}
                    </h6>
                  </div>
                  <div className={styles['adviserOffice']}>
                    <h5>Office</h5>
                    {adviserOffices[0] != null && (
                      <a href={`/${adviserOffices[0].full_slug}`}>
                        <h6>{adviserOffices[0].name}</h6>
                      </a>
                    )}
                  </div>
                  <div className={styles['speciality']}>
                    <h5>My Specialty</h5>
                    <span>
                      {Array.isArray(adviser?.content?.specialities) &&
                        (adviser.content.specialities as string[]).map(
                          (speciality: string, index: number) => {
                            const specialityLink = specialtyLinks.find(
                              (link) => link.name === speciality
                            )
                            return (
                              <React.Fragment key={index}>
                                <a href={specialityLink?.value ?? '#'}>{speciality}</a>
                                {index < (adviser.content.specialities as string[]).length - 1 &&
                                  ', '}
                              </React.Fragment>
                            )
                          }
                        )}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className={styles['loadMoreContainer']}>
          {totalAdvisers > advisers.length && (
            <PhnxButton
              type="button"
              variant="secondary"
              size="default"
              label={isLoading ? 'Loading...' : 'Load More'}
              onClick={loadMore}
              disabled={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default AdvisersView
